import type { ClientOutstandingOrder } from '@/renderer/services/accommodationService';
import { fetchCompanyClients } from '@/renderer/services/orderService';
import { fetchCreditBillsRows } from '@/renderer/services/reportsService';
import type { SalesReportCreditorView } from '@/renderer/types/report';
import type {
  ReportColumn,
  ReportExportColumn,
  ReportMetric,
} from '@/renderer/types/reports';
import {
  formatReportDate,
  formatReportMoney,
  formatReportUserName,
} from '@/renderer/util/reporting';
import { Button, DropdownSelect } from '@components/common';
import type { DropdownSelectOption } from '@components/common/DropdownSelect';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SettleCreditBillModal } from '../../sales/SettleCreditBillModal';
import { OrderDetailsModal } from './OrderDetailsModal';
import { ReportRecordsPage } from './ReportRecordsPage';

const CLIENT_PAGE_SIZE = 20;
const ALL_CLIENTS_OPTION: DropdownSelectOption = {
  value: '',
  label: 'All clients',
};

const buildLabel = (row: SalesReportCreditorView): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  if (row.client_name) return `${row.client_name} #${row.bill_number}`;
  return `#${row.bill_number}`;
};

const buildCreditBillMetrics = (
  rows: SalesReportCreditorView[],
): ReportMetric[] => {
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);
  const clientCount = new Set(rows.map((row) => row.client_id).filter(Boolean))
    .size;

  return [
    {
      label: 'Outstanding bills',
      value: rows.length.toLocaleString(),
      hint: 'Credit bills awaiting payment',
      tone: 'amber',
    },
    {
      label: 'Clients',
      value: clientCount.toLocaleString(),
      hint: 'Clients with outstanding credit',
      tone: 'blue',
    },
    {
      label: 'Total outstanding',
      value: formatReportMoney(totalAmount),
      hint: 'Sum of all unpaid credit bills',
      tone: 'slate',
    },
  ];
};

const creditBillExportColumns: ReportExportColumn<SalesReportCreditorView>[] = [
  {
    key: 'sale_date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  { key: 'bill', header: 'Bill', value: (row) => `#${row.bill_number}` },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'order', header: 'Order', value: (row) => buildLabel(row) },
  {
    key: 'client',
    header: 'Client',
    value: (row) => row.client_name || '',
  },
  {
    key: 'contact',
    header: 'Contact',
    value: (row) => row.client_contact || row.client_email || '—',
  },
  {
    key: 'user',
    header: 'Settled by',
    value: (row) =>
      formatReportUserName(row.created_by.first_name, row.created_by.last_name),
  },
  { key: 'notes', header: 'Notes', value: (row) => row.notes || '—' },
  {
    key: 'amount',
    header: 'Amount',
    value: (row) => formatReportMoney(row.amount),
  },
];

export const CreditBillsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [settlingBill, setSettlingBill] =
    useState<SalesReportCreditorView | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [clientOptions, setClientOptions] = useState<DropdownSelectOption[]>(
    [],
  );
  const [clientSearch, setClientSearch] = useState('');
  const [clientOffset, setClientOffset] = useState(0);
  const [hasMoreClients, setHasMoreClients] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const loadClientsPage = useCallback(
    async (search: string, offset: number, append: boolean) => {
      if (!currentUser?.company?.id) return;
      setIsLoadingClients(true);
      const res = await fetchCompanyClients({
        search,
        offset,
        limit: CLIENT_PAGE_SIZE + 1,
      });
      setIsLoadingClients(false);
      if (!res.success) return;
      const items = res.data?.clients ?? [];
      const hasMore = items.length > CLIENT_PAGE_SIZE;
      const opts = items
        .slice(0, CLIENT_PAGE_SIZE)
        .map((c) => ({ value: c.id, label: c.full_name }));
      setHasMoreClients(hasMore);
      setClientOptions((prev) => (append ? [...prev, ...opts] : opts));
    },
    [currentUser?.company?.id],
  );

  useEffect(() => {
    setClientOffset(0);
    loadClientsPage(clientSearch, 0, false);
  }, [clientSearch, loadClientsPage]);

  const handleClientSearch = useCallback((search: string) => {
    setClientSearch(search);
  }, []);

  const handleLoadMoreClients = useCallback(() => {
    const next = clientOffset + CLIENT_PAGE_SIZE;
    setClientOffset(next);
    loadClientsPage(clientSearch, next, true);
  }, [clientOffset, clientSearch, loadClientsPage]);

  const handleSettled = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const buildQueryParams = useCallback(
    ({ searchQuery: sq }: { searchQuery: string }) => ({
      search: sq.trim() || undefined,
      filters: {
        clientId: selectedClientId || undefined,
      },
      forceRefresh: refreshKey > 0 ? true : undefined,
    }),
    [selectedClientId, refreshKey],
  );

  const createColumns = useCallback(
    (
      onOpenOrder: (orderId: string) => void,
    ): ReportColumn<SalesReportCreditorView>[] => [
      {
        key: 'sale_date',
        label: 'Credit date',
        className: 'min-w-[9rem]',
        render: (row) => formatReportDate(row.sale_date),
      },
      {
        key: 'bill_number',
        label: 'Bill',
        render: (row) => (
          <button
            type="button"
            onClick={() => onOpenOrder(row.order_id)}
            className="font-semibold text-emerald-700 hover:underline"
          >
            #{row.bill_number}
          </button>
        ),
      },
      {
        key: 'outlet_name',
        label: 'Outlet',
        render: (row) => row.outlet_name,
      },
      {
        key: 'client_name',
        label: 'Client',
        className: 'min-w-[10rem]',
        render: (row) => row.client_name || '—',
      },
      {
        key: 'contact',
        label: 'Contact',
        className: 'min-w-[10rem]',
        render: (row) => row.client_contact || row.client_email || '—',
      },
      {
        key: 'created_by',
        label: 'Issued by',
        render: (row) =>
          formatReportUserName(
            row.created_by.first_name,
            row.created_by.last_name,
          ),
      },
      {
        key: 'notes',
        label: 'Notes',
        className: 'min-w-[12rem]',
        render: (row) => row.notes || '—',
      },
      {
        key: 'amount',
        label: 'Amount',
        align: 'right',
        render: (row) => (
          <span className="font-semibold text-red-600">
            {formatReportMoney(row.amount)}
          </span>
        ),
      },
      {
        key: 'settle',
        label: 'Action',
        align: 'center',
        className: 'min-w-[8rem]',
        render: (row) => (
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => setSettlingBill(row)}
            className="rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700 hover:bg-emerald-50"
          >
            Settle
          </Button>
        ),
      },
    ],
    [],
  );

  const columns = useMemo(
    () => createColumns(setSelectedOrderId),
    [createColumns],
  );

  const clientFilter = (
    <div className="min-w-[14rem]">
      <DropdownSelect
        value={selectedClientId}
        options={[ALL_CLIENTS_OPTION, ...clientOptions]}
        placeholder="All clients"
        searchable
        searchPlaceholder="Search clients…"
        onSearchChange={handleClientSearch}
        hasMoreOptions={hasMoreClients}
        isLoadingOptions={isLoadingClients}
        onLoadMoreOptions={handleLoadMoreClients}
        onChange={setSelectedClientId}
      />
    </div>
  );

  const settleOrder: ClientOutstandingOrder | null = settlingBill
    ? {
        id: settlingBill.id,
        order_id: settlingBill.order_id,
        bill_number: settlingBill.bill_number,
        sale_date: settlingBill.sale_date,
        table_name: settlingBill.table_name,
        section_name: settlingBill.section_name,
        amount: settlingBill.amount,
        payment_method_name: settlingBill.payment_method_name,
        notes: settlingBill.notes,
      }
    : null;

  return (
    <>
      <ReportRecordsPage
        key={refreshKey}
        title="Credit Bills"
        description="Find and settle outstanding credit bills. Bills appear here from the date they were issued until payment is recorded. Use the client filter or date range to narrow results. Settling a bill moves it to the payment day's sales report with the correct accounting entry."
        searchPlaceholder="Search by bill, client, outlet or notes"
        emptyTitle="No outstanding credit bills"
        emptyDescription="There are no CREDIT settlements in this range for the current filters. The client may have no outstanding bills, or all bills have already been settled."
        fetchRows={fetchCreditBillsRows}
        columns={columns}
        exportConfig={{
          fileNamePrefix: 'credit-bills',
          columns: creditBillExportColumns,
        }}
        buildMetrics={buildCreditBillMetrics}
        getRowKey={(row) => row.id}
        getSearchValue={(row) =>
          [
            row.bill_number,
            row.outlet_name,
            row.table_name,
            row.client_name,
            row.client_contact,
            row.client_email,
            row.notes,
            formatReportUserName(
              row.created_by.first_name,
              row.created_by.last_name,
            ),
          ]
            .filter(Boolean)
            .join(' ')
        }
        searchMode="server"
        buildQueryParams={buildQueryParams}
        toolbarContent={clientFilter}
      />

      <OrderDetailsModal
        isOpen={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />

      <SettleCreditBillModal
        isOpen={Boolean(settlingBill)}
        order={settleOrder}
        onClose={() => setSettlingBill(null)}
        onSettled={handleSettled}
      />
    </>
  );
};
